'use client';

import {
  checkWechatLogin,
  loginByPhone,
  PhoneLoginResponse,
  sendPhoneCode,
} from '@/app/actions/user';
import { getWechatQRCode } from '@/app/services/wechat';
import { Logo } from '@/components/icons';
import ModalDirective from '@/components/modal-directive';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useNewUserTrial } from '@/hooks/use-new-user-trial';
import { isMobileDevice, isWx } from '@/lib/device-utils';
import {
  REFERRAL_CODE_STORAGE_KEY,
  resolveReferralCode,
} from '@/lib/referral-code';
import { redirectToWxOauth } from '@/lib/wx-oauth';
import { sendGTMEvent } from '@next/third-parties/google';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

type TencentCaptchaConstructor = new (
  appId: string,
  callback: (res: CaptchaResult) => void,
  options?: Record<string, unknown>,
) => { show: () => void };

interface CaptchaResult {
  ret: number;
  ticket: string;
  randstr: string;
  CaptchaAppId?: string;
  bizState?: unknown;
  errorCode?: number;
  errorMessage?: string;
}

type LoginCardProps = {
  referralCode: string | null;
  onSuccess?: () => void;
  onCancel?: () => void;
};

export function LoginCard({
  referralCode,
  onSuccess,
  onCancel,
}: LoginCardProps) {
  const { points: trialPoints } = useNewUserTrial();
  // 微信内置浏览器中无法扫自己，改为展示一键授权按钮
  const [isInWxBrowser, setIsInWxBrowser] = useState(false);
  const [activeTab, setActiveTab] = useState<'wechat' | 'phone'>('wechat');
  const [qrCode, setQrCode] = useState('');
  const [qrExpired, setQrExpired] = useState(false);
  const pollRef = useRef<NodeJS.Timeout>(null);
  const expiryRef = useRef<NodeJS.Timeout>(null);
  const eventKeyRef = useRef('');
  const lockedRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const onCancelRef = useRef(onCancel);
  onSuccessRef.current = onSuccess;
  onCancelRef.current = onCancel;

  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [captchaReady, setCaptchaReady] = useState(false);
  const [captchaLoadFailed, setCaptchaLoadFailed] = useState(false);
  const countdownRef = useRef<NodeJS.Timeout>(null);

  const QR_EXPIRE_MS = 5 * 60 * 1000 - 5000; // 5 分钟
  const CAPTCHA_APP_ID = '191113126';

  const getSource = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('source') || params.get('utm_source') || 'browser';
  };

  const showPhoneSignupQrModal = () => {
    ModalDirective.open({
      title: '关注公众号领取后续服务',
      content: (
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">
            注册成功，欢迎关注 Suppr
            公众号，后续服务通知和使用指引会同步在公众号内发布。
          </p>
          <Image
            src="/img/qrcode_for_gh_7a8a6bf50ba2_258.jpg"
            alt="Suppr 公众号二维码"
            width={220}
            height={220}
            className="rounded-xl border"
          />
        </div>
      ),
    });
  };

  const onLoginSuccess = (result?: PhoneLoginResponse) => {
    toast.success('登录成功', { position: 'top-center' });
    sendGTMEvent({ event: 'register_success', value: getSource() });
    localStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
    onSuccessRef.current ? onSuccessRef.current() : onCancelRef.current?.();
    if (result?.is_new_user) {
      showPhoneSignupQrModal();
      return;
    }
  };

  // ========== WeChat QR Code ==========
  const getQRCode = async () => {
    try {
      clearInterval(pollRef.current ?? undefined);
      clearTimeout(expiryRef.current ?? undefined);
      setQrExpired(false);
      const { data } = await getWechatQRCode(
        resolveReferralCode(
          referralCode,
          localStorage.getItem(REFERRAL_CODE_STORAGE_KEY),
        ),
        getSource(),
      );
      setQrCode(data.qrcode_url);
      eventKeyRef.current = data.event_key;
      // 5 分钟后标记过期（轮询由 activeTab useEffect 控制启停）
      expiryRef.current = setTimeout(() => {
        clearInterval(pollRef.current ?? undefined);
        setQrExpired(true);
      }, QR_EXPIRE_MS);
    } catch (e) {}
  };

  const checkLogin = async () => {
    if (eventKeyRef.current) {
      const res = await checkWechatLogin(eventKeyRef.current);
      if (res && !lockedRef.current) {
        lockedRef.current = true;
        clearInterval(pollRef.current ?? undefined);
        clearTimeout(expiryRef.current ?? undefined);
        lockedRef.current = false;
        onLoginSuccess();
      }
    }
  };

  // 根据当前 tab 控制轮询启停：只有在 wechat tab 且二维码未过期时才轮询
  useEffect(() => {
    if (
      activeTab === 'wechat' &&
      !isInWxBrowser &&
      eventKeyRef.current &&
      !qrExpired
    ) {
      pollRef.current = setInterval(checkLogin, 2000);
    } else {
      clearInterval(pollRef.current ?? undefined);
    }
    return () => clearInterval(pollRef.current ?? undefined);
  }, [activeTab, isInWxBrowser, qrCode, qrExpired]);

  useEffect(() => {
    // 微信内置浏览器：不拉二维码（自己扫不了自己），改为一键授权，默认 wechat tab
    // 非微信移动端：默认手机号 tab（手机端扫码体验差）
    // 桌面端：默认 wechat tab（扫码体验好）
    if (isWx()) {
      setIsInWxBrowser(true);
    } else {
      getQRCode();
      if (isMobileDevice()) {
        setActiveTab('phone');
      }
    }

    // 加载腾讯云验证码 2.0 JS SDK
    const w = window as unknown as {
      TencentCaptcha?: TencentCaptchaConstructor;
    };
    const markCaptchaReady = () => {
      setCaptchaReady(true);
      setCaptchaLoadFailed(false);
    };
    const markCaptchaFailed = () => {
      setCaptchaReady(false);
      setCaptchaLoadFailed(true);
    };

    let script = document.getElementById(
      'tcaptcha-script',
    ) as HTMLScriptElement | null;
    if (w.TencentCaptcha) {
      markCaptchaReady();
    } else {
      if (!script) {
        script = document.createElement('script');
        script.id = 'tcaptcha-script';
        script.src = 'https://turing.captcha.qcloud.com/TJCaptcha.js';
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', markCaptchaReady);
      script.addEventListener('error', markCaptchaFailed);
    }

    return () => {
      script?.removeEventListener('load', markCaptchaReady);
      script?.removeEventListener('error', markCaptchaFailed);
      setQrCode('');
      eventKeyRef.current = '';
      clearInterval(pollRef.current ?? undefined);
      clearTimeout(expiryRef.current ?? undefined);
      clearInterval(countdownRef.current ?? undefined);
    };
  }, []);

  // ========== Phone Login ==========
  const captchaCallback = (res: CaptchaResult) => {
    if (res.ret === 0) {
      // errorCode 存在说明是容灾票据（trerror_前缀）
      if (res.errorCode) {
        toast.error('验证码加载异常，请刷新后重试', {
          position: 'top-center',
        });
        return;
      }
      doSendCode(res.ticket, res.randstr);
    }
    // ret === 2 用户主动关闭，不做处理
  };
  const loadErrorCallback = () => {
    const ticket =
      'trerror_1001_' + CAPTCHA_APP_ID + '_' + Math.floor(Date.now() / 1000);
    captchaCallback({
      ret: 0,
      randstr: '@' + Math.random().toString(36).substring(2),
      ticket,
      errorCode: 1001,
      errorMessage: 'jsload_error',
    });
  };

  const raiseCaptchaZIndex = () => {
    const lift = () => {
      document.body.style.pointerEvents = 'auto';

      const styleId = 'tcaptcha-login-modal-layer-fix';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          #tCaptchaMaskLayer {
            z-index: 2147483646 !important;
            pointer-events: auto !important;
          }

          #tcaptcha_transform_dy,
          #tCaptchaDyContent,
          [id^=tcaptcha]:not(#tCaptchaMaskLayer),
          [id^=tCaptcha]:not(#tCaptchaMaskLayer),
          [class*=tcaptcha],
          [class*=tCaptcha],
          iframe[src*=captcha],
          iframe[src*=turing] {
            z-index: 2147483647 !important;
            pointer-events: auto !important;
          }
        `;
        document.head.appendChild(style);
      }

      const layers = [
        document.getElementById('tcaptcha_transform_dy'),
        document.getElementById('tCaptchaMaskLayer'),
        ...Array.from(
          document.querySelectorAll<HTMLElement>(
            [
              '[id^=tcaptcha]',
              '[id^=tCaptcha]',
              '[class*=tcaptcha]',
              '[class*=tCaptcha]',
              'iframe[src*=captcha]',
              'iframe[src*=turing]',
            ].join(','),
          ),
        ),
      ].filter((el): el is HTMLElement => Boolean(el));

      layers.forEach((el) => {
        el.style.zIndex =
          el.id === 'tCaptchaMaskLayer' ? '2147483646' : '2147483647';
        el.style.pointerEvents = 'auto';
      });
    };

    [0, 100, 300, 800, 1500, 3000].forEach((delay) => {
      window.setTimeout(lift, delay);
    });
  };

  const showCaptchaAndSendCode = () => {
    if (!/^1[3-9]\d{9}$/.test(phoneNumber)) {
      toast.error('请输入正确的手机号', { position: 'top-center' });
      return;
    }
    const Captcha = (
      window as unknown as {
        TencentCaptcha?: TencentCaptchaConstructor;
      }
    ).TencentCaptcha;
    if (!Captcha) {
      toast.error(
        captchaLoadFailed
          ? '验证码加载失败，请刷新后重试'
          : '验证码组件加载中，请稍后再试',
        { position: 'top-center' },
      );
      return;
    }
    try {
      const captcha = new Captcha(CAPTCHA_APP_ID, captchaCallback, {});
      captcha.show();
      raiseCaptchaZIndex();
    } catch {
      loadErrorCallback();
    }
  };

  const doSendCode = async (ticket: string, randstr: string) => {
    setSendingCode(true);
    const { error } = await sendPhoneCode(phoneNumber, ticket, randstr);
    setSendingCode(false);
    if (error) {
      toast.error(error, { position: 'top-center' });
      return;
    }
    toast.success('验证码已发送', { position: 'top-center' });
    setCountdown(60);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current ?? undefined);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handlePhoneLogin = async () => {
    if (!/^1[3-9]\d{9}$/.test(phoneNumber)) {
      toast.error('请输入正确的手机号', { position: 'top-center' });
      return;
    }
    if (verificationCode.length !== 6) {
      toast.error('请输入6位验证码', { position: 'top-center' });
      return;
    }
    setPhoneLoading(true);
    try {
      const { data: res, error } = await loginByPhone(
        phoneNumber,
        verificationCode,
        resolveReferralCode(
          referralCode,
          localStorage.getItem(REFERRAL_CODE_STORAGE_KEY),
        ),
      );
      if (error) {
        toast.error(error, { position: 'top-center' });
      } else if (res) {
        onLoginSuccess(res);
      }
    } catch {
      toast.error('登录失败，请重试', { position: 'top-center' });
    } finally {
      setPhoneLoading(false);
    }
  };

  return (
    <div className="flex flex-col relative z-10">
      {/* 顶部 */}
      <div className="px-6 pt-8 pb-4 text-center">
        <div className="flex items-center justify-center gap-2.5 mb-2">
          <Logo size={26} />
          <h2 className="text-[22px] font-semibold tracking-tight">
            登录 Suppr
          </h2>
        </div>
        <div className="inline-flex items-center gap-2.5 rounded-full border bg-muted/50 px-4 py-1.5 text-[13px] text-muted-foreground animate-in fade-in slide-in-from-bottom-2 duration-700">
          <span className="relative flex h-1.5 w-1.5 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
          <span>
            注册即送{' '}
            <span className="font-medium text-foreground">7 天高级版</span> +{' '}
            <span className="font-medium text-foreground">
              {trialPoints.toLocaleString()}
            </span>{' '}
            积分
          </span>
        </div>
      </div>

      {/* 移动端 Tab */}
      <div className="md:hidden flex mx-6 mt-2 border-b">
        <button
          onClick={() => setActiveTab('wechat')}
          className={`flex-1 pb-2.5 text-[13px] font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'wechat'
              ? 'border-foreground text-foreground'
              : 'border-transparent text-muted-foreground'
          }`}
        >
          {isInWxBrowser ? '微信登录' : '微信扫码'}
        </button>
        <button
          onClick={() => setActiveTab('phone')}
          className={`flex-1 pb-2.5 text-[13px] font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'phone'
              ? 'border-foreground text-foreground'
              : 'border-transparent text-muted-foreground'
          }`}
        >
          手机号
        </button>
      </div>

      {/* 登录区域 */}
      <div className="flex flex-col md:flex-row items-start px-6 pt-5 pb-4 gap-5 md:gap-0">
        {/* 微信登录 */}
        <div
          className={`flex flex-col items-center md:pr-6 w-full md:w-[208px] ${
            activeTab !== 'wechat' ? 'hidden md:flex' : ''
          }`}
        >
          <span className="hidden md:block text-[13px] text-muted-foreground mb-3">
            {isInWxBrowser ? '微信一键登录' : '微信扫码登录'}
          </span>
          {isInWxBrowser ? (
            // 微信内置浏览器：展示一键授权按钮
            <div className="flex flex-col items-center justify-center w-full md:w-[168px] md:h-[168px] gap-3 py-2">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-[#07C160]/10">
                <svg
                  viewBox="0 0 1024 1024"
                  className="w-9 h-9 text-[#07C160]"
                  fill="currentColor"
                >
                  <path d="M664.250054 368.541681c10.015098 0 19.892049 0.732687 29.67281 1.795902-26.647917-122.810047-159.358451-214.077703-310.826188-214.077703-169.353083 0-308.085774 114.232694-308.085774 259.274068 0 83.708494 46.165436 152.460344 123.281791 205.78483l-30.80868 91.730191 107.688651-53.455469c38.558178 7.53665 69.459978 15.308108 107.924012 15.308108 9.66308 0 19.230993-0.470721 28.752858-1.225921-6.025227-20.36584-9.521864-41.723264-9.521864-63.862493C402.328693 476.632491 517.908058 368.541681 664.250054 368.541681zM498.62897 285.87389c23.200398 0 38.557154 15.120372 38.557154 38.061874 0 22.846334-15.356756 38.156256-38.557154 38.156256-23.107041 0-46.260842-15.309922-46.260842-38.156256C452.368128 300.994262 475.521929 285.87389 498.62897 285.87389zM283.016307 362.09201c-23.107041 0-46.402422-15.309922-46.402422-38.156256 0-22.941502 15.295381-38.061874 46.402422-38.061874 23.107041 0 38.46268 15.120372 38.46268 38.061874C321.479011 346.782088 306.123372 362.09201 283.016307 362.09201zM945.448458 606.151333c0-121.888048-123.258822-221.236753-261.683991-221.236753-146.57838 0-262.015349 99.348706-262.015349 221.236753 0 122.06301 115.436969 221.200114 262.015349 221.200114 30.66801 0 61.617359-7.609138 92.376423-15.262894l84.434538 45.800133-23.158558-76.17218C899.379551 735.257484 945.448458 674.90216 945.448458 606.151333zM598.772186 567.994292c-15.379213 0-30.827091-15.120372-30.827091-30.7197 0-15.4512 15.447878-30.7197 30.827091-30.7197 23.294779 0 38.550792 15.268499 38.550792 30.7197C637.322978 552.87392 622.066965 567.994292 598.772186 567.994292zM768.863778 567.994292c-15.28376 0-30.638904-15.120372-30.638904-30.7197 0-15.4512 15.355144-30.7197 30.638904-30.7197 23.107041 0 38.550792 15.268499 38.550792 30.7197C807.41457 552.87392 791.970819 567.994292 768.863778 567.994292z" />
                </svg>
              </div>
              <Button
                onClick={() => redirectToWxOauth('login')}
                className="w-full bg-[#07C160] hover:bg-[#06AD56] text-white"
              >
                微信一键登录
              </Button>
              <span className="text-[11px] text-muted-foreground/60 text-center">
                跳转至微信授权页面
              </span>
            </div>
          ) : (
            <>
              <div className="relative rounded-xl overflow-hidden bg-white p-1.5">
                {qrCode ? (
                  <Image
                    width={168}
                    height={168}
                    src={qrCode}
                    alt="微信登录二维码"
                    className={`block transition-all ${qrExpired ? 'blur-sm opacity-30' : ''}`}
                    sizes="168px"
                  />
                ) : (
                  <Skeleton className="h-42 w-42" />
                )}
                {qrExpired && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-neutral-700 text-xs mb-2 font-medium">
                      已过期
                    </span>
                    <Button
                      size="sm"
                      onClick={getQRCode}
                      className="rounded-full text-xs"
                    >
                      刷新
                    </Button>
                  </div>
                )}
              </div>
              <span className="text-[11px] text-muted-foreground/60 mt-2.5">
                {qrExpired ? '二维码已过期' : '打开微信扫一扫'}
              </span>
            </>
          )}
        </div>

        {/* 分隔线 */}
        <div className="hidden md:flex items-center self-stretch">
          <div className="w-px h-full bg-border" />
        </div>

        {/* 手机号登录 */}
        <div
          className={`flex-1 flex flex-col md:pl-6 w-full ${
            activeTab !== 'phone' ? 'hidden md:flex' : ''
          }`}
        >
          <span className="hidden md:block text-[13px] text-muted-foreground mb-3">
            手机验证码登录
          </span>
          <div className="flex flex-col gap-2.5">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground select-none z-10">
                +86
              </span>
              <Input
                type="tel"
                maxLength={11}
                placeholder="请输入手机号"
                value={phoneNumber}
                onChange={(e) =>
                  setPhoneNumber(e.target.value.replace(/\D/g, ''))
                }
                className="pl-11 h-10"
              />
            </div>
            <div className="flex gap-2">
              <Input
                type="text"
                maxLength={6}
                placeholder="验证码"
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(e.target.value.replace(/\D/g, ''))
                }
                className="flex-1 h-10"
              />
              <Button
                variant="outline"
                onClick={showCaptchaAndSendCode}
                disabled={countdown > 0 || sendingCode || !captchaReady}
                className="h-10 px-4"
              >
                {sendingCode
                  ? '发送中...'
                  : countdown > 0
                    ? `${countdown}s`
                    : captchaReady
                      ? '获取验证码'
                      : '验证码加载中'}
              </Button>
            </div>
            <Button
              onClick={handlePhoneLogin}
              disabled={phoneLoading}
              className="w-full h-10"
            >
              {phoneLoading ? '登录中...' : '登录 / 注册'}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground/60 text-center mt-2.5">
            未注册手机号将自动创建账号
          </p>
        </div>
      </div>

      {/* 协议 */}
      <div className="text-center text-[11px] text-muted-foreground/60 px-6 pb-6 pt-2">
        登录即表示同意
        <Link
          href="/terms"
          target="_blank"
          className="text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          用户协议
        </Link>
        {' 和 '}
        <Link
          href="/privacy"
          target="_blank"
          className="text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          隐私条款
        </Link>
      </div>
    </div>
  );
}
